/**
 * Vite Interstellar Environment Variables
 *
 * Use this configuration file to using specific environment variables within Interstellar.
 * Note that Vite requires variables to be prefixed with `VITE_` in order for the values to be exposed to `import.meta.env`
 *
 * There is a global mock of this file within `jest/setupTests.js`.
 * When introducing new environment variables, ensure that `jest/setupTests.js`is also updated.
 *
 * @see {@link https://vite.dev/guide/env-and-mode}
 * @see {@link file://./../jest/setupTests.js}
 */
import type { ServerBaseUrl } from '~/servers/types';

/**
 * VITE_APP_DATADOG_RUM_CLIENT_TOKEN
 *
 * Retrieves the DataDog client token from environment vars
 *
 * @see {@link file://./services/datadogService.ts}
 */
export const getDatadogClientToken = () =>
    import.meta.env.VITE_APP_DATADOG_RUM_CLIENT_TOKEN;

/**
 * VITE_APP_VERSION
 *
 * Retrieves the App version from environment vars
 *
 * @see {@link file://./services/datadogService.ts}
 */
export const getAppVersion = () => import.meta.env.VITE_APP_VERSION;

/**
 * VITE_DRIVER_COST_SERVICE_BASE_URL
 *
 * Retrieves the explicit Driver Cost service API base URL. When unset, driver
 * cost formula persistence remains disabled.
 */
export const getDriverCostServiceBaseUrl = () =>
    import.meta.env.VITE_DRIVER_COST_SERVICE_BASE_URL;

/**
 * VITE_LOCAL_*_SERVER
 *
 * Retrieves the local development server url overrides, keyed by `ServerBaseUrl` property.
 * Values are typically set in a git-ignored `.env.local` file, and are only read by the dev server
 * so a stray `.env.local` can never leak into a production build.
 *
 * @see {@link file://./servers/configs/development.ts}
 */
export const getLocalServerOverrides = (): Partial<ServerBaseUrl> => {
    if (!import.meta.env.DEV) return {};

    return {
        apiServer: import.meta.env.VITE_LOCAL_API_SERVER,
        auroraApiServer: import.meta.env.VITE_LOCAL_AURORA_API_SERVER,
        socketServer: import.meta.env.VITE_LOCAL_SOCKET_SERVER,
        auroraServer: import.meta.env.VITE_LOCAL_AURORA_SERVER,
        initialVsActualServer: import.meta.env.VITE_LOCAL_IVA_SERVER,
        equipment: import.meta.env.VITE_LOCAL_EQUIPMENT_SERVER,
        equipmentSocketServer: import.meta.env
            .VITE_LOCAL_EQUIPMENT_SOCKET_SERVER,
        vehicleProfile: import.meta.env.VITE_LOCAL_VEHICLE_PROFILE_SERVER,
        routePathGeometryServer: import.meta.env
            .VITE_LOCAL_ROUTE_PATH_GEOMETRY_SERVER,
        configurableWorkflowServer: import.meta.env
            .VITE_LOCAL_CONFIGURABLE_WORKFLOW_SERVER,
        driverCostServer: import.meta.env.VITE_LOCAL_DRIVER_COST_SERVER,
        // `VITE_TARGET_ENV` selects the banner service, so the local override only applies without it
        bannerService: import.meta.env.VITE_TARGET_ENV?.trim()
            ? undefined
            : import.meta.env.VITE_LOCAL_BANNER_SERVICE_SERVER
    };
};

/**
 * Allows the redux migration debugging messages to be displayed for all browser environments
 *
 * @see {@link file://./../src/store/store.ts}
 */
export const getReduxDebugMigration = () => true;
